import React, { useEffect } from 'react';
import { 
  Mic, 
  MicOff, 
  Video, 
  VideoOff, 
  Phone, 
  PhoneOff,
  SkipForward, 
  X, 
  ArrowLeft 
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useWebRTC } from '../hooks/useWebRTC';

export const VideoChat = ({ 
  socket,
  status, 
  partnerId,
  onNext, 
  onDisconnect, 
  onBack 
}) => {
  const {
    localStream,
    remoteStream,
    isCallActive,
    isConnecting,
    error,
    isAudioEnabled,
    isVideoEnabled,
    localVideoRef,
    remoteVideoRef,
    startCall,
    endCall,
    toggleAudio,
    toggleVideo,
  } = useWebRTC(socket, partnerId);

  useEffect(() => {
    if (status === 'matched' && partnerId && !isCallActive && !isConnecting) {
      startCall();
    }
  }, [status, partnerId, isCallActive, isConnecting]);

  const getStatusText = () => {
    switch (status) {
      case 'waiting':
        return 'Looking for a stranger...';
      case 'matched':
        if (isConnecting) return 'Connecting to video call...';
        if (isCallActive) return 'Connected to a stranger';
        return 'Setting up video call...';
      case 'partner_disconnected':
        return 'Stranger disconnected';
      default:
        return 'Connecting...';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'waiting':
        return 'text-yellow-600';
      case 'matched':
        return isCallActive ? 'text-green-600' : 'text-blue-600';
      case 'partner_disconnected':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const handleNext = () => {
    endCall();
    onNext();
  };

  const handleDisconnect = () => {
    endCall();
    onDisconnect();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 flex flex-col p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onBack}
            className="hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Video Chat</h1>
            <p className={`text-sm ${getStatusColor()}`}>
              {getStatusText()}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {status === 'matched' && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleNext}
              className="bg-white/80 hover:bg-white"
            >
              <SkipForward className="w-4 h-4 mr-2" />
              Next
            </Button>
          )}
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={handleDisconnect}
          >
            <X className="w-4 h-4 mr-2" />
            Stop
          </Button>
        </div>
      </div>

      {/* Video Area */}
      <Card className="flex-1 flex flex-col bg-white/80 backdrop-blur-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {status === 'waiting' ? (
              <div className="flex items-center gap-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-purple-500 rounded-full pulse-dot"></div>
                  <div className="w-2 h-2 bg-purple-500 rounded-full pulse-dot"></div>
                  <div className="w-2 h-2 bg-purple-500 rounded-full pulse-dot"></div>
                </div>
                <span>Finding someone for you...</span>
              </div>
            ) : status === 'matched' ? (
              isCallActive ? 'Video call active' : 'Connecting to video call...'
            ) : status === 'partner_disconnected' ? (
              'Stranger has disconnected'
            ) : (
              'Connecting...'
            )}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="flex-1 flex flex-col">
          {/* Error Display */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Video Container */}
          <div className="flex-1 relative bg-gray-900 rounded-lg overflow-hidden min-h-0">
            {/* Waiting State */}
            {status === 'waiting' && (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-600 to-pink-600">
                <div className="text-center text-white">
                  <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-lg font-semibold">Searching for someone...</p>
                  <p className="text-sm opacity-80">Please wait while we find you a chat partner</p>
                </div>
              </div>
            )}

            {/* Remote Video */}
            {remoteStream && (
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="absolute inset-0 w-full h-full object-contain"
              />
            )}

            {/* Local Video */}
            {localStream && (
              <div className="absolute top-4 right-4 w-32 h-24 md:w-48 md:h-36 bg-gray-800 rounded-lg overflow-hidden border-2 border-white shadow-lg">
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-contain"
                />
              </div>
            )}

            {/* No Video State */}
            {!localStream && status === 'matched' && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                <div className="text-center text-white">
                  <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-semibold">Camera not available</p>
                  <p className="text-sm opacity-80">Please allow camera access to start video chat</p>
                </div>
              </div>
            )}

            {/* Disconnected State */}
            {status === 'partner_disconnected' && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                <div className="text-center text-white">
                  <PhoneOff className="w-16 h-16 mx-auto mb-4 text-red-400" />
                  <p className="text-lg font-semibold">Stranger disconnected</p>
                  <p className="text-sm opacity-80 mb-4">The other person has left the chat</p>
                  <Button 
                    variant="outline" 
                    onClick={handleNext}
                    className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                  >
                    Find another stranger
                  </Button>
                </div>
              </div>
            )}

            {/* Connection Status Overlay */}
            {isConnecting && status === 'matched' && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-lg font-semibold">Connecting...</p>
                </div>
              </div>
            )}
          </div>

          {/* Controls */}
          {(localStream || status === 'matched') && (
            <div className="flex justify-center gap-4 mt-4 p-4 bg-gray-800 rounded-lg">
              <Button
                variant={isAudioEnabled ? "default" : "destructive"}
                size="icon"
                onClick={toggleAudio}
                className="rounded-full"
              >
                {isAudioEnabled ? (
                  <Mic className="w-5 h-5" />
                ) : (
                  <MicOff className="w-5 h-5" />
                )}
              </Button>
              
              <Button
                variant={isVideoEnabled ? "default" : "destructive"}
                size="icon"
                onClick={toggleVideo}
                className="rounded-full"
              >
                {isVideoEnabled ? (
                  <Video className="w-5 h-5" />
                ) : (
                  <VideoOff className="w-5 h-5" />
                )}
              </Button>
              
              <Button
                variant="destructive"
                size="icon"
                onClick={handleDisconnect}
                className="rounded-full"
              >
                <Phone className="w-5 h-5" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}; 